/*
 * Firebase App Check.
 *
 * WHY THIS EXISTS
 *
 * The Cloud Functions in front of the database do real work — the honeypot, the
 * per-IP and per-tournament rate limits, the field validation. None of it
 * applies to a client that skips them and talks to Firestore directly, which
 * anyone can do: firestore.rules lets an unauthenticated caller create a
 * `registrations` document for any public tournament (that is how the in-app
 * "Register" button works for spectators), and the Firebase web config needed to
 * address the project is served to every browser that loads the site.
 *
 * App Check closes that path. The SDK attaches an attestation token to every
 * Firestore, Storage and callable-Functions request, and once enforcement is on
 * in the console, requests without a valid one are refused at Google's edge —
 * before a rule is evaluated and before anything is billed.
 *
 * WHAT IT DOES NOT COVER
 *
 * The registration widget runs on OTHER PEOPLE'S websites (embed/registration-form.js,
 * and the /r/<id> page framed cross-origin). reCAPTCHA site keys are bound to a
 * domain list, so those pages cannot mint a valid token, and App Check must
 * therefore NEVER be enforced on the public registration endpoints. That is
 * fine: the widget posts to the Registration API, which runs on the Admin SDK
 * and does its own honeypot and rate limiting. App Check is the guard on the
 * DIRECT-to-Firestore path only, which is exactly the one that had nothing.
 *
 * SETTING IT UP  (until this is done, this module is a deliberate no-op)
 *
 *   1. Google Cloud console -> reCAPTCHA -> create a key, type "Website",
 *      score-based (v3). Add the domains: zugzwang.no, www.zugzwang.no, and
 *      tournamentmanager-6df09.web.app / .firebaseapp.com if previews are used.
 *   2. Firebase console -> Build -> App Check -> Apps -> register the web app
 *      with that reCAPTCHA v3 site key.
 *   3. Paste the SITE KEY below. It is public — it ships in the page, like the
 *      Firebase config above it. The SECRET key stays in the Firebase console
 *      and must never appear in this repository.
 *   4. Deploy. Then WATCH, do not enforce: App Check -> APIs shows verified vs
 *      unverified request counts. Leave it a week. Real traffic from an old
 *      cached page still counts as unverified, and enforcing early locks out
 *      whoever has not reloaded.
 *   5. When the unverified share is down to noise, turn on enforcement for
 *      Cloud Firestore. Storage too, if the same holds there.
 *
 * Enforcement is a console setting, not a code change — which also means it can
 * be switched off again in seconds if something was missed.
 */

import { initializeAppCheck, ReCaptchaV3Provider } from 'https://www.gstatic.com/firebasejs/12.12.1/firebase-app-check.js';

// Step 3 above. Empty until then, and empty means this module does nothing.
const RECAPTCHA_V3_SITE_KEY = '';

const isLocal = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);

let started = null;

/**
 * Start App Check for an initialized Firebase app. Safe to call more than once —
 * the first call wins — and safe to call before a site key exists.
 *
 * Returns the App Check instance, or null when it is not configured.
 */
export function initAppCheck(app) {
    if (started !== null) return started;

    if (!RECAPTCHA_V3_SITE_KEY) {
        // Not an error, and not worth a console.warn on every page load in
        // production: the app is fully functional without it right up until
        // enforcement is switched on, and that cannot happen before a key exists.
        started = null;
        return null;
    }

    // Local development has no reCAPTCHA domain to attest against. The debug
    // flag makes the SDK mint a debug token instead and print it once; register
    // that token under App Check -> Apps -> Manage debug tokens to let the
    // emulator through an enforced project.
    //
    // This MUST be set before initializeAppCheck, and it is gated on the
    // hostname so a production page can never take the path.
    if (isLocal) {
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    try {
        started = initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(RECAPTCHA_V3_SITE_KEY),
            // Refresh the token in the background so a long editing session does
            // not start failing writes an hour in.
            isTokenAutoRefreshEnabled: true
        });
    } catch (err) {
        // A failure here must not take the app down. Before enforcement that is
        // trivially true; after it, the requests would fail anyway, and a broken
        // page on top of that helps nobody diagnose it.
        console.error('App Check failed to initialise; continuing without it.', err);
        started = null;
    }
    return started;
}
